import { Chart } from "@antv/g2";
import * as _ from "@antv/util";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Input, theme } from "antd";
import { SearchOutlined } from "@ant-design/icons";

function Heatmap({
  data = {},
  x = "datetime",
  y = "name",
  color = "value",
  height = 500,
  yAxisTitle = "Device Name",
  yLabelFormatter = (d) => d,
  xAxisTitle = "Date Time",
  xLabelFormatter = (d) => d,
  xValueFormatter = (d) => d,
  yValueFormatter = (d) => d,
  colorValueFormatter = (d) => d,
}) {
  const container = _.uniqueId("heatmap-");
  const chartRef = useRef(null);
  const [searchString, setSearchString] = useState(null);
  const ref = useRef(null);
  const { token } = theme.useToken();

  const heatmapData = useMemo(() => {
    let result = data;
    if (searchString != null && searchString != undefined && data != null) {
      result = data?.filter(
        (item) => item[y].toUpperCase().indexOf(searchString.toUpperCase()) > -1
      );
    }
    return result;
  }, [data, searchString]);

  useEffect(() => {
    const chart = new Chart({
      container: container,
      height: height,
      autoFit: true,
      marginBottom: 30,
    });

    chart
      .cell()
      .data(heatmapData)
      .encode("x", x)
      .encode("y", y)
      .encode("color", color)
      .transform({ type: "sortX", by: "x", reverse: true })
      .style("inset", 0.5)
      .axis("y", {
        title: yAxisTitle,
        labelAutoRotate: false,
        labelFill: token.colorTextBase,
        labelFormatter: yLabelFormatter,
        labelAutoEllipsis: true,
      })
      .axis("x", {
        title: xAxisTitle,
        position: "top",
        labelFill: token.colorTextBase,
        labelFormatter: xLabelFormatter,
        labelAutoEllipsis: true,
      })
      .scale("color", {
        palette: "blues",
        relations: [
          [(d) => d === null, "#eee"],
          [0, "#fff"],
        ],
      })
      // .scrollbar("x", {
      //   style: {
      //     thumbFill: token.colorTextBase,
      //     thumbFillOpacity: 0.7,
      //   },
      //   ratio: 1,
      // })
      // .scrollbar("y", {
      //   style: {
      //     thumbFill: token.colorTextBase,
      //     thumbFillOpacity: 0.7,
      //   },
      //   ratio: 0.8
      // })
      .tooltip({
        title: (d) => d[y],
        items: [
          {
            name: "Time",
            channel: "x",
            valueFormatter: xValueFormatter,
          },
          {
            name: "Usage",
            channel: "color",
            valueFormatter: colorValueFormatter,
          },
        ],
      });

    document.getElementById(container).innerHTML = "";
    chartRef.current = chart;
    chart.render();

    const chartContainer = document.getElementById(container);
    const resizeObserver = new ResizeObserver(() => {
      chartRef.current.forceFit();
    });
    resizeObserver.observe(chartContainer.parentElement);

    return () => {
      chartRef.current.destroy();
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    let chart = chartRef.current;

    const ySize = [...new Set(heatmapData.map((item) => item[y]))].length;
    const xSize = [...new Set(heatmapData.map((item) => item[x]))].length;

    chart.clear();
    chart
      .cell()
      .data(heatmapData)
      .encode("x", x)
      .encode("y", y)
      .encode("color", color)
      .transform({ type: "sortX", by: "x", reverse: true })
      .transform({ type: "sortY", by: "color", reverse: true })
      .slider("x", null)
      .style("inset", 0.5)
      .axis("y", {
        title: yAxisTitle,
        titleFill: token.colorTextBase,
        labelAutoRotate: false,
        labelFill: token.colorTextBase,
        labelFormatter: yLabelFormatter,
        labelAutoHide: false,
        labelAutoEllipsis: true,
      })
      .axis("x", {
        title: xAxisTitle,
        titleFill: token.colorTextBase,
        position: "top",
        labelFill: token.colorTextBase,
        labelFormatter: xLabelFormatter,
        labelAutoEllipsis: true,
      })
      .scale("color", {
        palette: "blues",
        relations: [
          [(d) => d === null, "#eee"],
          [0, "#fff"],
        ],
      })
      .scrollbar(
        "y",
        ySize > 10
          ? {
              ratio: 10 / ySize,
              style: {
                thumbFill: token.colorTextBase,
                thumbFillOpacity: 0.3,
              },
            }
          : false
      )
      .scrollbar(
        "x",
        xSize > 10
          ? {
              ratio: 10 / xSize,
              style: {
                thumbFill: token.colorTextBase,
                thumbFillOpacity: 0.3,
              },
            }
          : false
      )
      .tooltip({
        title: (d) => d[y],
        items: [
          {
            name: "Time",
            channel: "x",
            valueFormatter: xValueFormatter,
          },
          {
            name: "Usage",
            channel: "color",
            valueFormatter: colorValueFormatter,
          },
        ],
      });

    chartRef.current = chart;
    chart.render();
  }, [token, yAxisTitle, xAxisTitle]);

  useEffect(() => {
    chartRef.current.changeData(heatmapData);
  }, [heatmapData]);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div id={container}></div>
      <Input
        style={{
          position: "absolute",
          width: 180,
          right: 5,
          top: 22,
        }}
        allowClear
        placeholder="Search"
        onChange={(e) => setSearchString(e.target.value)}
        addonAfter={<SearchOutlined />}
      />
    </div>
  );
}

export default Heatmap;
